import type { RefObject } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useTranslation } from 'react-i18next';
import { CountrySelect } from '@/components/CountrySelect';
import { countryFlagClass, getCountryName } from '@/lib/countries';
import { Search } from 'lucide-react';
import { useFideAutofill } from '@/lib/useFideAutofill';
import { FideAutofillDialog } from '@/components/fide/FideAutofillDialog';
import type { FideAutofillTarget } from '@/lib/fide-autofill';
import { bmabTitleLabel, fideTitleLabel } from '@/lib/game-labels';
import {
  BMAB_TITLES,
  FIDE_TITLES,
  PROFILE_BIO_MAX_LENGTH,
  type BmabTitleCode,
} from '@swiss-magic/shared-types';

// Story 075 (M3) — only render avatars that come from a real
// https URL. Today the upload flow always produces Supabase storage
// URLs, but if the backend ever returns a user-supplied URL on this
// field, javascript: / data: URIs become a latent XSS vector.
// Falling through to the placeholder is fine — it's the no-avatar
// look users already see.
function isSafeImageUrl(url: string | null | undefined): url is string {
  if (!url) return false;
  try {
    const u = new URL(url);
    if (u.protocol === 'https:') return true;
    // Local-dev carve-out: Supabase storage serves over
    // http://127.0.0.1:54321 in the local stack. Mirrors the
    // browser secure-context spec (loopback is considered
    // potentially-trustworthy). Real deployments always serve
    // avatars over HTTPS so production behaviour is unchanged.
    if (u.protocol === 'http:' && (u.hostname === '127.0.0.1' || u.hostname === 'localhost')) {
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

export interface ProfileFormValues {
  firstName: string;
  lastName: string;
  city: string;
  country: string;
  bio: string;
  avatarUrl: string | null;
  chessRating: string;
  chessFideId: string;
  chessClub: string;
  sex: '' | 'm' | 'f';
  birthYear: string;
  chessTitle: string;
  backgammonClub: string;
  bmabTitle: BmabTitleCode | '';
}

export interface ProfileFormHandlers {
  setFirstName: (v: string) => void;
  setLastName: (v: string) => void;
  setCity: (v: string) => void;
  setCountry: (v: string) => void;
  setBio: (v: string) => void;
  setChessRating: (v: string) => void;
  setChessFideId: (v: string) => void;
  setChessClub: (v: string) => void;
  setSex: (v: '' | 'm' | 'f') => void;
  setBirthYear: (v: string) => void;
  setChessTitle: (v: string) => void;
  setBackgammonClub: (v: string) => void;
  setBmabTitle: (v: BmabTitleCode | '') => void;
  avatarInputRef: RefObject<HTMLInputElement | null>;
  cameraInputRef: RefObject<HTMLInputElement | null>;
  onAvatarChange: (file: File | null) => void;
  onAvatarRemove: () => void;
}

interface ProfileFormFieldsProps {
  values: ProfileFormValues;
  // When edit is provided, fields are interactive and avatar mutation buttons
  // render. When omitted, fields are readOnly / disabled and the avatar block
  // shows just the image (or placeholder) — the director's read-only view.
  edit?: {
    handlers: ProfileFormHandlers;
    errors: Record<string, string>;
    submitting: boolean;
  };
}

export function ProfileFormFields({ values, edit }: ProfileFormFieldsProps) {
  const { t, i18n } = useTranslation('player');
  const { t: tc } = useTranslation('common');
  const editable = edit !== undefined;

  // Story 098 — FIDE ID autofill (chess). Reads the FIDE ID from the Chess
  // section and fills the rating-grade fields; conflicting filled fields are
  // confirmed via the dialog.
  const fideAutofill = useFideAutofill('player');
  function applyFideAutofill(v: Partial<FideAutofillTarget>) {
    const h = edit?.handlers;
    if (!h) return;
    if (v.firstName !== undefined) h.setFirstName(v.firstName);
    if (v.lastName !== undefined) h.setLastName(v.lastName);
    if (v.sex !== undefined) h.setSex(v.sex as '' | 'm' | 'f');
    if (v.birthYear !== undefined) h.setBirthYear(v.birthYear);
    if (v.chessTitle !== undefined) h.setChessTitle(v.chessTitle);
    if (v.rating !== undefined) h.setChessRating(v.rating);
    if (v.country !== undefined) h.setCountry(v.country);
  }
  function lookupFide() {
    void fideAutofill.lookup(
      values.chessFideId,
      {
        firstName: values.firstName,
        lastName: values.lastName,
        sex: values.sex,
        birthYear: values.birthYear,
        chessTitle: values.chessTitle,
        rating: values.chessRating,
        country: values.country,
      },
      applyFideAutofill,
    );
  }
  const mastershipTitles = BMAB_TITLES.filter((tit) => tit.group === 'mastership');
  const proficiencyTitles = BMAB_TITLES.filter((tit) => tit.group === 'proficiency');
  const requiredSuffix = editable ? t('profile.fields.required_suffix') : '';

  return (
    <div className="space-y-8">
      {/* Avatar + bio (untitled top section) */}
      <section className="flex flex-col gap-4 sm:flex-row sm:gap-6">
        <div className="flex flex-col items-center gap-2 sm:items-start">
          {isSafeImageUrl(values.avatarUrl) ? (
            <img
              data-testid="profile-avatar"
              src={values.avatarUrl}
              alt={t('profile.fields.avatar_alt')}
              className="h-24 w-24 rounded-full border bg-muted/30 object-cover"
            />
          ) : (
            <div
              data-testid="profile-avatar-placeholder"
              className="flex h-24 w-24 items-center justify-center rounded-full border text-xs text-muted-foreground"
            >
              {t('profile.fields.avatar_placeholder')}
            </div>
          )}
          {editable && (
            <>
              <input
                ref={edit.handlers.avatarInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                data-testid="profile-avatar-input"
                className="hidden"
                onChange={(e) =>
                  edit.handlers.onAvatarChange(e.target.files?.[0] ?? null)
                }
              />
              {/* Camera capture: mobile browsers honor `capture` and open
                  the front camera; desktop ignores it and falls back to
                  the file picker, so the button stays useful everywhere. */}
              <input
                ref={edit.handlers.cameraInputRef}
                type="file"
                accept="image/*"
                capture="user"
                data-testid="profile-avatar-camera-input"
                className="hidden"
                onChange={(e) =>
                  edit.handlers.onAvatarChange(e.target.files?.[0] ?? null)
                }
              />
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  data-testid="profile-avatar-upload"
                  onClick={() => edit.handlers.avatarInputRef.current?.click()}
                  disabled={edit.submitting}
                >
                  {values.avatarUrl
                    ? t('profile.fields.avatar_replace')
                    : t('profile.fields.avatar_upload')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  data-testid="profile-avatar-camera"
                  onClick={() => edit.handlers.cameraInputRef.current?.click()}
                  disabled={edit.submitting}
                >
                  {t('profile.fields.avatar_take_photo')}
                </Button>
                {values.avatarUrl && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    data-testid="profile-avatar-remove"
                    onClick={edit.handlers.onAvatarRemove}
                    disabled={edit.submitting}
                  >
                    {t('profile.fields.avatar_remove')}
                  </Button>
                )}
              </div>
            </>
          )}
        </div>

        <div className="flex-1 space-y-2">
          <Label htmlFor="bio">{t('profile.fields.bio_label')}</Label>
          <textarea
            id="bio"
            data-testid="profile-bio-input"
            value={values.bio}
            readOnly={!editable}
            maxLength={PROFILE_BIO_MAX_LENGTH}
            onChange={(e) => edit?.handlers.setBio(e.target.value)}
            rows={5}
            className="flex w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30"
          />
          {editable && (
            <p
              data-testid="profile-bio-counter"
              className="text-right text-xs text-muted-foreground"
            >
              {values.bio.length}/{PROFILE_BIO_MAX_LENGTH}
            </p>
          )}
        </div>
      </section>

      {/* General */}
      <section className="space-y-4">
        <h3
          data-testid="profile-section-general"
          className="text-sm font-semibold uppercase tracking-wide text-muted-foreground"
        >
          {t('profile.fields.section_general')}
        </h3>
        <div className="grid gap-6 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="firstName">{t('profile.fields.first_name_label')}{requiredSuffix}</Label>
            <Input
              id="firstName"
              data-testid="profile-first-name-input"
              value={values.firstName}
              readOnly={!editable}
              onChange={(e) => edit?.handlers.setFirstName(e.target.value)}
            />
            {editable && edit.errors.firstName && (
              <p
                data-testid="profile-first-name-error"
                className="text-sm text-destructive"
              >
                {edit.errors.firstName}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="lastName">{t('profile.fields.last_name_label')}{requiredSuffix}</Label>
            <Input
              id="lastName"
              data-testid="profile-last-name-input"
              value={values.lastName}
              readOnly={!editable}
              onChange={(e) => edit?.handlers.setLastName(e.target.value)}
            />
            {editable && edit.errors.lastName && (
              <p
                data-testid="profile-last-name-error"
                className="text-sm text-destructive"
              >
                {edit.errors.lastName}
              </p>
            )}
          </div>
        </div>

        <div className="grid gap-6 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="city">{t('profile.fields.city_label')}</Label>
            <Input
              id="city"
              data-testid="profile-city-input"
              value={values.city}
              readOnly={!editable}
              onChange={(e) => edit?.handlers.setCity(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="country">{t('profile.fields.country_label')}</Label>
            {editable ? (
              <CountrySelect
                data-testid="profile-country-input"
                value={values.country}
                onChange={(code) => edit?.handlers.setCountry(code)}
              />
            ) : (
              <p
                data-testid="profile-country-input"
                data-country-code={values.country || ''}
                className="rounded border bg-muted/30 px-3 py-2 text-sm"
              >
                {values.country ? (
                  <>
                    <span className={countryFlagClass(values.country)} />{' '}
                    {getCountryName(values.country, i18n.language)}
                  </>
                ) : (
                  t('profile.fields.none')
                )}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Chess */}
      <section className="space-y-4 border-t pt-6">
        <h3
          data-testid="profile-section-chess"
          className="text-sm font-semibold uppercase tracking-wide text-muted-foreground"
        >
          {t('profile.fields.section_chess')}
        </h3>
        <div className="grid gap-6 sm:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor="chessRating">{t('profile.fields.chess_rating_label')}</Label>
            <Input
              id="chessRating"
              type="number"
              data-testid="profile-chess-rating-input"
              value={values.chessRating}
              readOnly={!editable}
              onChange={(e) => edit?.handlers.setChessRating(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="chessFideId">{t('profile.fields.chess_fide_id_label')}</Label>
            <Input
              id="chessFideId"
              type="number"
              data-testid="profile-chess-fide-id-input"
              value={values.chessFideId}
              readOnly={!editable}
              onChange={(e) => edit?.handlers.setChessFideId(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="chessClub">{t('profile.fields.chess_club_label')}</Label>
            <Input
              id="chessClub"
              data-testid="profile-chess-club-input"
              value={values.chessClub}
              readOnly={!editable}
              onChange={(e) => edit?.handlers.setChessClub(e.target.value)}
            />
          </div>
        </div>
      </section>

      {/* FIDE — story 096. Rating-grade fields required for a FIDE
          tournament report; collected here so they're available when a
          player enters a FIDE-rated event. */}
      <section className="space-y-4 border-t pt-6">
        <h3
          data-testid="profile-section-fide"
          className="text-sm font-semibold uppercase tracking-wide text-muted-foreground"
        >
          {t('profile.fields.section_fide')}
        </h3>
        {editable && (
          <div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="profile-fide-lookup-button"
              disabled={fideAutofill.looking}
              onClick={lookupFide}
            >
              <Search className="mr-1 h-4 w-4" />
              {fideAutofill.looking
                ? t('fide_autofill.looking')
                : t('fide_autofill.lookup_button')}
            </Button>
            <p className="mt-1 text-xs text-muted-foreground">
              {t('fide_autofill.lookup_hint')}
            </p>
          </div>
        )}
        <div className="grid gap-6 sm:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor="sex">{t('profile.fields.sex_label')}</Label>
            <select
              id="sex"
              data-testid="profile-sex-select"
              value={values.sex}
              disabled={!editable}
              onChange={(e) =>
                edit?.handlers.setSex(e.target.value as '' | 'm' | 'f')
              }
              className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm dark:bg-input/30"
            >
              <option value="">{t('profile.fields.sex_none')}</option>
              <option value="m">{t('profile.fields.sex_m')}</option>
              <option value="f">{t('profile.fields.sex_f')}</option>
            </select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="birthYear">
              {t('profile.fields.birth_year_label')}
            </Label>
            <Input
              id="birthYear"
              type="number"
              data-testid="profile-birth-year-input"
              value={values.birthYear}
              readOnly={!editable}
              onChange={(e) => edit?.handlers.setBirthYear(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="chessTitle">
              {t('profile.fields.chess_title_label')}
            </Label>
            <select
              id="chessTitle"
              data-testid="profile-chess-title-select"
              value={values.chessTitle}
              disabled={!editable}
              onChange={(e) => edit?.handlers.setChessTitle(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm dark:bg-input/30"
            >
              <option value="">{t('profile.fields.chess_title_none')}</option>
              {FIDE_TITLES.map((tit) => (
                <option key={tit.code} value={tit.code}>
                  {fideTitleLabel(tit.code, tit.label, tc)} ({tit.code})
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {/* Backgammon */}
      <section className="space-y-4 border-t pt-6">
        <h3
          data-testid="profile-section-backgammon"
          className="text-sm font-semibold uppercase tracking-wide text-muted-foreground"
        >
          {t('profile.fields.section_backgammon')}
        </h3>
        <div className="grid gap-6 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="bmabTitle">{t('profile.fields.bmab_title_label')}</Label>
            <select
              id="bmabTitle"
              data-testid="profile-bmab-title-select"
              value={values.bmabTitle}
              disabled={!editable}
              onChange={(e) =>
                edit?.handlers.setBmabTitle(e.target.value as BmabTitleCode | '')
              }
              className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm dark:bg-input/30"
            >
              <option value="">{t('profile.fields.bmab_title_none')}</option>
              <optgroup label={t('profile.fields.bmab_group_mastership')}>
                {mastershipTitles.map((tit) => (
                  <option key={tit.code} value={tit.code}>
                    {bmabTitleLabel(tit.code, tit.label, tc)} ({tit.code})
                  </option>
                ))}
              </optgroup>
              <optgroup label={t('profile.fields.bmab_group_proficiency')}>
                {proficiencyTitles.map((tit) => (
                  <option key={tit.code} value={tit.code}>
                    {bmabTitleLabel(tit.code, tit.label, tc)} ({tit.code})
                  </option>
                ))}
              </optgroup>
            </select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="backgammonClub">{t('profile.fields.backgammon_club_label')}</Label>
            <Input
              id="backgammonClub"
              data-testid="profile-backgammon-club-input"
              value={values.backgammonClub}
              readOnly={!editable}
              onChange={(e) =>
                edit?.handlers.setBackgammonClub(e.target.value)
              }
            />
          </div>
        </div>
      </section>

      <FideAutofillDialog
        conflicts={fideAutofill.conflicts}
        i18nNamespace="player"
        onOverwrite={fideAutofill.confirmOverwrite}
        onCancel={fideAutofill.dismiss}
      />
    </div>
  );
}
